/**
 * TaskChangeSection - Section wrapper for task change cards.
 *
 * Sorts: CREATED -> UPDATED -> DELETED -> SAME.
 */

import { useMemo } from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import type {
  ChangedTaskEntity,
  ChangedTaskUpdateEntity,
} from '@/services/api/generated/crossMatterChangesService';

import { CHANGE_TYPE_ORDER } from './changeTypeConfig';
import { TaskChangeCard } from './TaskChangeCard';

interface TaskChangeSectionProps {
  tasks: ChangedTaskEntity[];
  taskUpdates?: ChangedTaskUpdateEntity[];
}

export const TaskChangeSection = ({
  tasks,
  taskUpdates = [],
}: TaskChangeSectionProps) => {
  const sorted = useMemo(
    () =>
      [...tasks].sort(
        (a, b) =>
          CHANGE_TYPE_ORDER[a.change_type] -
          CHANGE_TYPE_ORDER[b.change_type],
      ),
    [tasks],
  );

  // Group task updates by task_id
  const tuByTask = useMemo(
    () =>
      taskUpdates.reduce<Record<string, ChangedTaskUpdateEntity[]>>(
        (map, tu) => {
          const taskId = (tu.current ?? tu.old)?.task_id;
          if (taskId) {
            (map[taskId] ??= []).push(tu);
          }
          return map;
        },
        {},
      ),
    [taskUpdates],
  );

  if (tasks.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold text-gray-900">Tasks</h3>
        <Badge color="zinc" className="text-xs">
          {tasks.length}
        </Badge>
      </div>
      <div className="space-y-2">
        {sorted.map((task) => (
          <TaskChangeCard
            key={task.entity_id}
            entity={task}
            taskUpdates={tuByTask[task.entity_id]}
          />
        ))}
      </div>
    </div>
  );
};
